import { useEffect, useRef, useState } from 'react';
import { subscribeReviewActivity } from '../services/reviewActivityPoller';
import { POLL_SCHEDULE } from '../utils/pollSchedule';

// N-21 / REQ-6133 (#609): how often the change probe runs while a review surface is
// open. Review is a minutes-scale human activity, not a trading feed — 10s bounds a
// reviewer's staleness well inside the time it takes them to read an item and form an
// opinion, which is the window in which "I didn't see your rejection" actually happens.
//
// #697: the cadence now lives in the shared poll schedule, which also carries the phase
// OFFSET that keeps this tick off the heartbeat's and the notification badge's. Kept
// exported at its original name because existing tests and callers reference it.
export const REVIEW_ACTIVITY_INTERVAL_MS = POLL_SCHEDULE.reviewActivity.intervalMs;

/**
 * N-21 / REQ-6133 (#609): poll the project's review-activity probe and report when
 * another reviewer has changed something.
 *
 * Returns `{ marker, changeCount }`. `changeCount` increments once per OBSERVED change,
 * so a consumer can use it directly as an effect dependency — a raw marker string works
 * too, but a counter makes "refetch on change" impossible to get subtly wrong (the
 * first observation must NOT count as a change; see below).
 *
 * Why polling and not SSE — this is a considered answer, not a placeholder:
 * `streaming_manager.py` is a per-request `EventSourceResponse` wrapping an in-process
 * generator. It is a response mechanism, not a broker; there is nothing to publish an
 * approval event into, and there is no pub/sub in the product (no Redis, no Postgres
 * LISTEN/NOTIFY). The backend also runs multiple uvicorn workers, so an in-process
 * fan-out would reach only the clients that happened to connect to the SAME worker —
 * and would fail SILENTLY, which is the worst possible failure for a requirement whose
 * entire purpose is "you can trust what you are looking at". When a broker exists, the
 * probe endpoint is the one thing that gets replaced; this hook's contract does not.
 *
 * Costs are deliberately bounded:
 * - **One request per tick for the WHOLE app** (#697). Every instance of this hook
 *   subscribes to a single shared poller (`services/reviewActivityPoller`), which polls
 *   the batch endpoint once for the union of every subscriber's projects. It used to
 *   issue one request per watched project per instance, and with four mount sites — two
 *   of which watch every membership, on views the shell never unmounts — that reached
 *   ~144 requests/minute for a user in 12 projects and made the dev deployment
 *   unusable. Both terms had to go: the per-project fan-out AND the per-instance timer.
 * - **Off the other pollers' phase** (#697). The shared poller ticks on a wall-clock
 *   grid whose offset is disjoint from the heartbeat's and the notification badge's —
 *   see `utils/pollSchedule.js`, where a test proves the tick sets never intersect over
 *   a full cycle. Mount-time phase made this impossible to reason about at all.
 * - **Hidden tabs do not poll.** A backgrounded tab has no reviewer looking at it, so
 *   its polling is pure server load. On becoming visible it probes IMMEDIATELY rather
 *   than waiting out the interval, because that is exactly the moment a reviewer's view
 *   is most likely to be stale.
 * - **A failed probe is silent.** Live refresh is an enhancement over the reload that
 *   already worked; it must never surface an error or spin. Failures simply leave the
 *   marker where it was and the next tick retries.
 *
 * Takes a LIST of project ids, not one. The requirements tree is not scoped to a single
 * project — `activeProject` is a user SELECTION and is routinely null while the tree
 * shows every visible project grouped together (verified in the real app: the probe
 * effect ran once with `projectId === null` and never again, so nothing was ever
 * polled). A hook keyed on the active project would therefore have refreshed nothing on
 * the surface that needs it most. Caller passes the projects actually on screen; a
 * change in ANY of them is a change worth refetching for.
 *
 * @param {string[]|null} projectIds projects to watch; polling is off when empty.
 * @param {{enabled?: boolean}} [opts]
 */
export default function useReviewActivity(projectIds, opts = {}) {
    const { enabled = true } = opts || {};
    // Deps must be a VALUE, not an array identity — a caller computing the list inline
    // would otherwise restart the timer on every render and never complete an interval.
    const idsKey = (Array.isArray(projectIds) ? projectIds : [projectIds])
        .filter(Boolean).sort().join(',');
    const [state, setState] = useState({ marker: null, changeCount: 0 });
    // The last marker SEEN, held in a ref so the polling effect never re-subscribes
    // when it moves — restarting the timer on every change would drift the cadence.
    const lastMarkerRef = useRef(null);

    useEffect(() => {
        // A change to the WATCHED SET is not "a change" — reset, so the first probe of
        // the new set establishes a baseline instead of reporting a phantom edit.
        lastMarkerRef.current = null;
        setState({ marker: null, changeCount: 0 });

        if (!idsKey || !enabled) return undefined;
        const ids = idsKey.split(',');

        let cancelled = false;

        // #697: subscribe to the shared poller instead of owning a timer. The timing,
        // the batching, the visibility standdown and the failure silence all live there
        // now — one place, one request per tick for every consumer in the app. What
        // stays HERE is the per-consumer change semantics below, which must remain
        // per-consumer: a project changing in the dashboard's watched set must not make
        // the open item's Breakdown tab refetch.
        const unsubscribe = subscribeReviewActivity(ids, (marker) => {
            if (cancelled || !marker) return;
            const previous = lastMarkerRef.current;
            lastMarkerRef.current = marker;
            // The FIRST observation is a baseline, never a change — counting it would
            // make every mount refetch immediately, which is the cost the probe exists
            // to avoid.
            setState((prev) => ({
                marker,
                changeCount: previous !== null && previous !== marker
                    ? prev.changeCount + 1
                    : prev.changeCount,
            }));
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [idsKey, enabled]);

    return state;
}
